import { useState, useMemo } from 'react';
import QuotingHoursControl from '../components/QuotingHoursControl';
import { quotingLabel, defaultOperatorId } from '../lib/config-quoting.mjs';
import {
  useConfigOperators, useConfigOperator,
  useOperatorPauseStatus, usePauseOperator, useResumeOperator,
  useBlacklistRules, useAddBlacklistRule, useRemoveBlacklistRule,
} from '../lib/api';

// Human labels + short hints for each surfaced config key.
const FIELD_META = {
  ACTIVE:                            { label: 'Active',                          hint: 'When false, this operator is excluded from default batch runs.' },
  NO_INTERNATIONAL:                  { label: 'No international',               hint: 'When true, any leg with a non-domestic airport is auto-declined in Phase 2 preflight.' },
  FLEET_GROUPS:                      { label: 'Fleet groups',                    hint: 'Quote-matching groups. Phase 2 only quotes within the same group.' },
  PAUSED_FLEET_GROUPS:               { label: 'Paused fleet groups',             hint: 'Groups temporarily withheld from quoting.' },
  PAUSED_TAILS:                      { label: 'Paused tails',                    hint: 'Tails withheld from quoting (excluded from Phase 1 extraction set).' },
  TAIL_SUBSTITUTIONS:                { label: 'Tail substitutions',              hint: 'requested → substituted. Quotes flow through the substitute; original tail is silently skipped on Avinode unless /decline --force is used.' },
  EFFECTIVE_RATE_FLOORS:             { label: 'NET FLIGHT rate floors',          hint: 'Per-class minimum $/hr. Phase 2 declines any quote below the floor (red X to Avinode).' },
  TAIL_RATE_FLOORS:                  { label: 'Tail-specific rate floors',       hint: 'Overrides EFFECTIVE_RATE_FLOORS for the listed tails.' },
  MID_MIN_FLIGHT_MINUTES:            { label: 'Midsize/Heavy minimum flight (min)', hint: 'Mid/Heavy jets require occupied flight time ≥ this. DB-sourced.' },
  EXCLUDED_DOMESTIC_AIRPORTS:        { label: 'Excluded domestic airports',      hint: 'Phase 1 auto-declines + posts red X immediately for any trip touching these.' },
  LIGHT_JET_EXCLUDED_AIRPORTS:       { label: 'Light Jet excluded airports',     hint: 'Runway too short for Light Jets. Heavy/Mid still processed.' },
  PERMIT_LEAD_TIME_HOURS:            { label: 'Permit lead-time (hours)',        hint: 'Per-country minimum lead time before international permit can be filed. EAS only.' },
  ASAP_INTERNATIONAL_APPROVED_PREFIXES: { label: 'ASAP intl approved ICAO prefixes', hint: 'For trips < 24h out, only legs touching these prefixes are allowed internationally.' },
  OPERATING_HOURS:                   { label: 'Operating hours',                 hint: 'Local time window when the scheduler will fire batches.' },
  SENTINEL_DIRECT_LOGIN:             { label: 'Sentinel direct login',           hint: 'When true, operator has its own Sentinel account; when false, lives under a host operator.' },
  AVINODE_POSTING_TAIL:              { label: 'Avinode posting tail',            hint: 'Single tail used to post prices when operator has no Avinode marketplace seat.' },
};

function fmtValue(value) {
  if (value === null || value === undefined) return <span className="text-slate-400">—</span>;
  if (typeof value === 'boolean') return <span className={value ? 'text-emerald-700' : 'text-rose-700'}>{String(value)}</span>;
  if (typeof value === 'number')  return <span className="font-mono">{value}</span>;
  if (typeof value === 'string')  return <span className="font-mono break-all">{value}</span>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-slate-400">(empty)</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((v, i) => (
          <span key={i} className="px-2 py-0.5 text-xs font-mono bg-slate-100 rounded">{String(v)}</span>
        ))}
      </div>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return <span className="text-slate-400">(empty)</span>;
    return (
      <table className="text-xs border-collapse">
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k} className="border-b last:border-0">
              <td className="pr-3 py-0.5 font-mono text-slate-600 align-top">{k}</td>
              <td className="py-0.5 align-top">{fmtValue(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return <span className="font-mono">{JSON.stringify(value)}</span>;
}

function FieldRow({ name, value }) {
  const meta = FIELD_META[name] || { label: name, hint: null };
  return (
    <div className="border-b last:border-0 py-3">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <div className="font-semibold text-sm">{meta.label}</div>
        <code className="text-xs text-slate-400">{name}</code>
      </div>
      {meta.hint && <div className="text-xs text-slate-500 mb-2">{meta.hint}</div>}
      <div className="text-sm pl-1">{fmtValue(value)}</div>
    </div>
  );
}

// Live pause/resume control for one operator. Pause stops ALL new quoting
// (Avinode batch, listener, CR) within ~1 cycle; reversible. Reason required to
// pause; who/why/when surfaced from the audit trail.
function PauseControl({ operatorId }) {
  const { data, isLoading } = useOperatorPauseStatus(operatorId);
  const pause = usePauseOperator();
  const resume = useResumeOperator();
  const [reason, setReason] = useState('');

  const paused = !!data?.quoting_paused;
  const last = data?.last_change;
  const busy = pause.isPending || resume.isPending;

  const onPause = () => {
    const r = reason.trim();
    if (!r) return;
    pause.mutate({ operatorId, reason: r }, { onSuccess: () => setReason('') });
  };
  const onResume = () => {
    resume.mutate({ operatorId, reason: reason.trim() || undefined }, { onSuccess: () => setReason('') });
  };

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="font-semibold text-sm">Quoting status</div>
        {isLoading ? (
          <span className="text-xs text-slate-400">…</span>
        ) : paused ? (
          <span className="px-2 py-0.5 text-xs font-semibold rounded bg-rose-100 text-rose-700">PAUSED</span>
        ) : (
          <span className="px-2 py-0.5 text-xs font-semibold rounded bg-emerald-100 text-emerald-700">ACTIVE</span>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Pause stops all new quoting for this operator (Avinode batch, listener, and CR) within ~1 cycle.
        Reversible; in-flight quotes still validate.
      </p>

      {/* The latest audit row is only the PAUSE reason when it is actually a pause.
          Rendering it unconditionally presented a RESUME as the current cause: on
          2026-08-13 raber read “Paused by cli at 2026-06-29 — CR batch halted …
          resume normal quoting”, six weeks stale and semantically inverted, because
          the flag had been set by a direct settings write that logged nothing.
          Never invent an attribution — say the record is missing (SQP-106a). */}
      {paused && last?.action === 'pause' && (
        <div className="text-xs text-slate-600 mb-3 bg-rose-50 border border-rose-100 rounded p-2">
          Paused by <span className="font-mono">{last.actor || 'unknown'}</span> at {last.at}
          {last.reason && <> — <span className="italic">“{last.reason}”</span></>}
        </div>
      )}
      {paused && last?.action !== 'pause' && (
        <div className="text-xs text-amber-800 mb-3 bg-amber-50 border border-amber-200 rounded p-2">
          ⚠️ Paused with <strong>no audit record</strong> — the flag was set outside the CLI and
          dashboard (a direct <span className="font-mono">settings</span> write), so who paused it
          and why were never recorded.
          {last && (
            <> The last recorded change was a <strong>{last.action}</strong> by{' '}
              <span className="font-mono">{last.actor || 'unknown'}</span> at {last.at}
              {last.reason && <> — <span className="italic">“{last.reason}”</span></>}, which is
              <strong> not</strong> the reason for the current pause.</>
          )}
        </div>
      )}
      {!paused && last && (
        <div className="text-xs text-slate-400 mb-3">
          Last change: {last.action} by <span className="font-mono">{last.actor || 'unknown'}</span> at {last.at}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={paused ? 'Reason to resume (optional)' : 'Reason to pause (required)'}
          className="flex-1 min-w-[220px] px-2 py-1 text-sm border border-slate-300 rounded"
        />
        {paused ? (
          <button
            onClick={onResume}
            disabled={busy}
            className="px-3 py-1 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {resume.isPending ? 'Resuming…' : 'Resume quoting'}
          </button>
        ) : (
          <button
            onClick={onPause}
            disabled={busy || !reason.trim()}
            className="px-3 py-1 text-sm rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {pause.isPending ? 'Pausing…' : 'Pause quoting'}
          </button>
        )}
      </div>
      {(pause.isError || resume.isError) && (
        <div className="text-xs text-rose-700 mt-2">{(pause.error || resume.error)?.message}</div>
      )}
    </div>
  );
}

// Per-operator request blacklist. Add a rule (broker + routing + departure
// window) and matching requests are HELD for manual review — never quoted or
// posted — across Avinode + webhook. Reason required; rules are soft-deleted.
const EMPTY_RULE = { brokerMatchType: 'email_domain', brokerMatchValue: '', airports: '', dateFrom: '', dateTo: '', reason: '' };

function BlacklistControl({ operatorId }) {
  const { data, isLoading } = useBlacklistRules(operatorId);
  const add = useAddBlacklistRule();
  const remove = useRemoveBlacklistRule();
  const [form, setForm] = useState(EMPTY_RULE);

  const rules = data?.rules || [];
  const activeCount = rules.filter((r) => r.active).length;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canAdd = form.brokerMatchValue.trim() && form.airports.trim() && form.dateFrom && form.dateTo && form.reason.trim();

  const onAdd = () => {
    if (!canAdd) return;
    add.mutate({ operatorId, ...form }, { onSuccess: () => setForm(EMPTY_RULE) });
  };

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="font-semibold text-sm">Request blacklist</div>
        <span className="px-2 py-0.5 text-xs font-semibold rounded bg-slate-100 text-slate-600">{activeCount} active</span>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Block a broker + routing for a departure window. Matching requests are{' '}
        <span className="font-semibold text-rose-700">HELD (not quoted)</span> across Avinode + webhook, tagged for manual review.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <label className="text-xs text-slate-600">Broker match
          <select value={form.brokerMatchType} onChange={set('brokerMatchType')} className="mt-0.5 w-full px-2 py-1 text-sm border border-slate-300 rounded">
            <option value="email_domain">Email domain (Avinode)</option>
            <option value="broker_id">Broker ID (webhook)</option>
          </select>
        </label>
        <label className="text-xs text-slate-600">{form.brokerMatchType === 'email_domain' ? 'Email domain' : 'Broker ID'}
          <input type="text" value={form.brokerMatchValue} onChange={set('brokerMatchValue')}
            placeholder={form.brokerMatchType === 'email_domain' ? 'aircharterservice.com' : 'broker-id'}
            className="mt-0.5 w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
        </label>
        <label className="text-xs text-slate-600">Airports (≥2, comma-sep)
          <input type="text" value={form.airports} onChange={set('airports')} placeholder="KAPA,KJZI"
            className="mt-0.5 w-full px-2 py-1 text-sm border border-slate-300 rounded font-mono" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-slate-600">From
            <input type="date" value={form.dateFrom} onChange={set('dateFrom')} className="mt-0.5 w-full px-2 py-1 text-sm border border-slate-300 rounded" />
          </label>
          <label className="text-xs text-slate-600">To
            <input type="date" value={form.dateTo} onChange={set('dateTo')} className="mt-0.5 w-full px-2 py-1 text-sm border border-slate-300 rounded" />
          </label>
        </div>
        <label className="text-xs text-slate-600 sm:col-span-2">Reason (required)
          <input type="text" value={form.reason} onChange={set('reason')} placeholder="Why block this broker / route / window?"
            className="mt-0.5 w-full px-2 py-1 text-sm border border-slate-300 rounded" />
        </label>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <button onClick={onAdd} disabled={!canAdd || add.isPending}
          className="px-3 py-1 text-sm rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">
          {add.isPending ? 'Adding…' : 'Add blacklist rule'}
        </button>
        {add.isError && <span className="text-xs text-rose-700">{add.error?.message}</span>}
      </div>

      {isLoading ? (
        <div className="text-xs text-slate-400">Loading…</div>
      ) : rules.length === 0 ? (
        <div className="text-xs text-slate-400">No blacklist rules.</div>
      ) : (
        <div className="space-y-2">
          {rules.map((r) => (
            <div key={r.id} className={`text-xs border rounded p-2 ${r.active ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200 opacity-60'}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">
                  {r.active ? <span className="text-rose-700">● BLOCKED</span> : <span className="text-slate-500">○ removed</span>}{' '}
                  {r.broker_match_type}=<span className="font-mono">{r.broker_match_value}</span>
                </span>
                {r.active && (
                  <button onClick={() => remove.mutate({ operatorId, ruleId: r.id })} disabled={remove.isPending}
                    className="px-2 py-0.5 rounded bg-slate-200 hover:bg-slate-300 disabled:opacity-50">Remove</button>
                )}
              </div>
              <div className="mt-1 font-mono">{(r.airports || []).join(' ↔ ')} · {r.date_from} → {r.date_to}</div>
              {r.reason && <div className="mt-0.5 italic text-slate-600">“{r.reason}”</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Config() {
  const { data: opsData, isLoading: opsLoading } = useConfigOperators();
  const operators = opsData?.operators || [];
  const defaultOp = useMemo(() => defaultOperatorId(operators), [operators]);
  const [selectedOp, setSelectedOp] = useState(null);
  const activeOp = selectedOp || defaultOp;

  const { data, isLoading, isError, error } = useConfigOperator(activeOp);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Configuration</h1>
        <p className="text-sm text-slate-600">
          Read-only view of the operator config the pipeline is actually using. Edits require a code change + deploy.
        </p>
      </div>

      {/* Operator picker */}
      <div className="card p-3 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-600 mr-2">Operator:</span>
        {opsLoading && <span className="text-sm text-slate-500">Loading…</span>}
        {operators.map(op => (
          <button
            key={op.operator_id}
            onClick={() => setSelectedOp(op.operator_id)}
            className={`px-3 py-1 text-sm rounded-md border capitalize transition-colors ${
              activeOp === op.operator_id
                ? 'bg-sentinel-navy text-white border-sentinel-navy'
                : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
            }`}
          >
            {op.operator_name || op.operator_id}
            {quotingLabel(op) && <span className="ml-1 text-xs opacity-70 normal-case">({quotingLabel(op)})</span>}
          </button>
        ))}
      </div>

      {/* Live pause/resume control for the selected operator */}
      {activeOp && <PauseControl operatorId={activeOp} />}

      {/* Quoting hours (SQP-332): window for AUTOMATED quoting; self-contained component */}
      {activeOp && <QuotingHoursControl key={activeOp} operatorId={activeOp} />}

      {/* Per-operator request blacklist (broker + routing + date window → HOLD) */}
      {activeOp && <BlacklistControl operatorId={activeOp} />}

      {isLoading && <div className="text-slate-500">Loading config…</div>}
      {isError && <div className="text-rose-700">Error: {error.message}</div>}

      {data?.config && (
        <div className="card p-4">
          <div className="text-xs text-slate-400 mb-3">
            {data.cached ? 'Cached (≤30s old)' : 'Live'}
          </div>
          {Object.entries(data.config).map(([k, v]) => <FieldRow key={k} name={k} value={v} />)}
        </div>
      )}
    </div>
  );
}
